import { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
} from '@chakra-ui/react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TicketService } from '@/lib/services/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import type { OpenProcurementTask, TicketPart, TicketPartStatus } from '@/types/parentTicket'
import {
  getPartPrimaryTaskId,
  getPartTaskNumber,
  isPartLinkedToTask,
} from '@/lib/utils/ticketPartLinkage'
import { ToolPartNameInput } from '@/components/forms/parentTicket/ToolPartNameInput'
import { PROCUREMENT_STATUS_LABELS } from './tasks/PartProcurementPanel'

type Props = {
  ticketId: string
  isReadOnly?: boolean
  onPartsChange?: () => void
  onOpenProcurementTask?: (taskId: number) => void
  hideTitle?: boolean
  compactTable?: boolean
}

const STATUS_COLORS: Record<TicketPartStatus, string> = {
  pending: 'gray',
  ordered: 'blue',
  available: 'green',
  not_available: 'red',
  received: 'purple',
}

export default function PartsInfoSection({
  ticketId,
  isReadOnly = false,
  onPartsChange,
  onOpenProcurementTask,
  hideTitle = false,
  compactTable = false,
}: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [parts, setParts] = useState<TicketPart[]>([])
  const [openProcurementTask, setOpenProcurementTask] = useState<OpenProcurementTask | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [sending, setSending] = useState(false)
  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure()
  const { isOpen: isChoiceOpen, onOpen: onChoiceOpen, onClose: onChoiceClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const [editingPart, setEditingPart] = useState<TicketPart | null>(null)
  const [partToDelete, setPartToDelete] = useState<TicketPart | null>(null)
  const [formPartName, setFormPartName] = useState('')
  const [formQuantity, setFormQuantity] = useState('')
  const [formUnit, setFormUnit] = useState('')
  const [formNotes, setFormNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const fetchParts = useCallback(async () => {
    if (!ticketId) return
    setLoading(true)
    const res = await TicketService.getTicketParts(ticketId)
    setLoading(false)
    if (res.success && res.data) {
      setParts(res.data)
      setOpenProcurementTask(res.openProcurementTask ?? null)
    } else {
      setParts([])
      setOpenProcurementTask(null)
    }
  }, [ticketId])

  useEffect(() => {
    void fetchParts()
  }, [fetchParts])

  const resetForm = () => {
    setEditingPart(null)
    setFormPartName('')
    setFormQuantity('')
    setFormUnit('')
    setFormNotes('')
  }

  const openAdd = () => {
    resetForm()
    onFormOpen()
  }

  const openEdit = (part: TicketPart) => {
    setEditingPart(part)
    setFormPartName(part.part_name)
    setFormQuantity(part.quantity ?? '')
    setFormUnit(part.unit ?? '')
    setFormNotes(part.notes ?? '')
    onFormOpen()
  }

  const handleSubmit = async () => {
    if (!ticketId || !formPartName.trim()) {
      showError(new Error('Part name is required'))
      return
    }
    setSubmitting(true)
    const payload = {
      part_name: formPartName.trim(),
      quantity: formQuantity.trim() || null,
      unit: formUnit.trim() || null,
      notes: formNotes.trim() || null,
    }
    const res = editingPart
      ? await TicketService.updateTicketPart(ticketId, editingPart.id, payload)
      : await TicketService.createTicketPart(ticketId, payload)
    setSubmitting(false)
    if (res.success) {
      showSuccess(editingPart ? 'Part updated' : 'Part added')
      onFormClose()
      resetForm()
      await fetchParts()
      onPartsChange?.()
    } else {
      showError(new Error(res.message ?? 'Failed to save'))
    }
  }

  const requestDelete = (part: TicketPart) => {
    if (isPartLinkedToTask(part)) {
      showError(new Error('This part is linked to a procurement task — remove it from the task first.'))
      return
    }
    setPartToDelete(part)
    onDeleteOpen()
  }

  const closeDeleteModal = () => {
    onDeleteClose()
    setPartToDelete(null)
  }

  const confirmDelete = async () => {
    if (!ticketId || !partToDelete) return
    setDeleting(true)
    const res = await TicketService.deleteTicketPart(ticketId, partToDelete.id)
    setDeleting(false)
    if (res.success) {
      showSuccess('Part removed')
      closeDeleteModal()
      onFormClose()
      resetForm()
      setSelectedIds((prev) => {
        const next = new Set(prev)
        next.delete(partToDelete.id)
        return next
      })
      await fetchParts()
      onPartsChange?.()
    } else {
      showError(new Error(res.message ?? 'Failed to remove'))
    }
  }

  const toggleSelect = (partId: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(partId)
      else next.delete(partId)
      return next
    })
  }

  const runBulkConvert = async (existingChildTicketId?: number | null) => {
    if (!ticketId || selectedIds.size === 0) return
    setSending(true)
    const partIds = Array.from(selectedIds)
    const res = await TicketService.bulkConvertPartsToTask(ticketId, partIds, existingChildTicketId)
    setSending(false)
    onChoiceClose()
    if (res.success && res.data) {
      const taskLabel = res.data.task_number ?? `task #${res.data.child_ticket_id}`
      showSuccess(`${partIds.length} part(s) sent to ${taskLabel}`)
      setSelectedIds(new Set())
      await fetchParts()
      onPartsChange?.()
    } else {
      showError(new Error(res.message ?? 'Failed to send parts to procurement'))
    }
  }

  const handleSendToProcurement = () => {
    if (selectedIds.size === 0) return
    if (openProcurementTask?.id) {
      onChoiceOpen()
      return
    }
    void runBulkConvert()
  }

  const renderProcurementChip = (part: TicketPart) => {
    const taskId = getPartPrimaryTaskId(part)
    if (!taskId) return <Text color={colors.textSecondary}>—</Text>
    const label = getPartTaskNumber(part, taskId) ?? `Task #${taskId}`
    if (onOpenProcurementTask) {
      return (
        <Badge
          as="button"
          colorScheme="blue"
          variant="subtle"
          cursor="pointer"
          textTransform="none"
          onClick={() => onOpenProcurementTask(taskId)}
        >
          {label}
        </Badge>
      )
    }
    return (
      <Badge colorScheme="blue" variant="subtle" textTransform="none">
        {label}
      </Badge>
    )
  }

  if (!ticketId) return null

  return (
    <Box>
      <HStack justify="space-between" align="center" mb={3}>
        {!hideTitle ? (
          <Text fontSize="xs" fontWeight="700" color={colors.textSecondary} textTransform="uppercase" letterSpacing="wider">
            Tools / Parts
          </Text>
        ) : (
          <Box />
        )}
        {!isReadOnly && (
          <Button leftIcon={<Plus size={14} />} size="sm" colorScheme="blue" onClick={openAdd}>
            Add parts
          </Button>
        )}
      </HStack>

      <VStack align="stretch" spacing={2}>
        {loading ? (
          <Text fontSize="sm" color={colors.textSecondary}>
            Loading...
          </Text>
        ) : parts.length === 0 ? (
          <Text fontSize="sm" color={colors.textSecondary}>
            No parts recorded. {!isReadOnly && 'Click "Add parts" to add one.'}
          </Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                {!isReadOnly && <Th w="36px" />}
                <Th>Part name</Th>
                <Th>Qty</Th>
                <Th>Unit</Th>
                <Th>Status</Th>
                {!compactTable && <Th>Part code</Th>}
                <Th>Procurement</Th>
                {!isReadOnly && <Th>Actions</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {parts.map((p) => {
                const status = p.status ?? 'pending'
                const linked = isPartLinkedToTask(p)
                return (
                  <Tr key={p.id}>
                    {!isReadOnly && (
                      <Td>
                        <Checkbox
                          isChecked={selectedIds.has(p.id)}
                          isDisabled={linked}
                          title={linked ? 'Already linked to a task' : undefined}
                          onChange={(e) => toggleSelect(p.id, e.target.checked)}
                        />
                      </Td>
                    )}
                    <Td>{p.part_name}</Td>
                    <Td>{p.quantity ?? '—'}</Td>
                    <Td>{p.unit ?? '—'}</Td>
                    <Td>
                      <Badge colorScheme={STATUS_COLORS[status]} variant="subtle" textTransform="none">
                        {PROCUREMENT_STATUS_LABELS[status]}
                      </Badge>
                    </Td>
                    {!compactTable && <Td>{p.part_code ?? '—'}</Td>}
                    <Td>{renderProcurementChip(p)}</Td>
                    {!isReadOnly && (
                      <Td>
                        <HStack spacing={1}>
                          <IconButton
                            aria-label="Edit part"
                            size="sm"
                            variant="ghost"
                            icon={<Pencil size={14} />}
                            onClick={() => openEdit(p)}
                          />
                          <IconButton
                            aria-label="Delete part"
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            icon={<Trash2 size={14} />}
                            onClick={() => requestDelete(p)}
                          />
                        </HStack>
                      </Td>
                    )}
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        )}
      </VStack>

      {!isReadOnly && parts.length > 0 ? (
        <HStack justify="space-between" mt={3} flexWrap="wrap" gap={2}>
          <Text fontSize="sm" color={colors.textSecondary}>
            {selectedIds.size} selected
          </Text>
          <Button
            size="sm"
            colorScheme="blue"
            isDisabled={selectedIds.size === 0}
            isLoading={sending}
            onClick={handleSendToProcurement}
          >
            Send to procurement →
          </Button>
        </HStack>
      ) : null}

      <Modal isOpen={isFormOpen} onClose={() => { onFormClose(); resetForm() }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingPart ? 'Edit part' : 'Add part'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>Tool / part name</FormLabel>
                <ToolPartNameInput value={formPartName} onChange={(name) => setFormPartName(name)} placeholder="Start typing to search the catalog…" />
              </FormControl>
              <HStack align="flex-start">
                <FormControl>
                  <FormLabel>Quantity</FormLabel>
                  <Input type="number" min={0} value={formQuantity} onChange={(e) => setFormQuantity(e.target.value)} placeholder="2" />
                </FormControl>
                <FormControl>
                  <FormLabel>Unit</FormLabel>
                  <Input value={formUnit} onChange={(e) => setFormUnit(e.target.value)} placeholder="pcs, sets, box" />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="Optional" rows={2} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => { onFormClose(); resetForm() }}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={() => void handleSubmit()} isLoading={submitting}>
              {editingPart ? 'Save' : 'Add part'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isChoiceOpen} onClose={onChoiceClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send to procurement</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm">
              An open procurement task already exists on this ticket (
              <strong>{openProcurementTask?.task_number ?? `task #${openProcurementTask?.id}`}</strong>
              ). Add the selected parts to it, or start a new task?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={onChoiceClose}>
              Cancel
            </Button>
            <Button variant="outline" mr={2} isLoading={sending} onClick={() => void runBulkConvert(null)}>
              Create new task
            </Button>
            <Button colorScheme="blue" isLoading={sending} onClick={() => void runBulkConvert(openProcurementTask?.id)}>
              Add to {openProcurementTask?.task_number ?? 'existing task'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDeleteOpen} onClose={closeDeleteModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>
              {partToDelete
                ? `Do you really want to delete "${partToDelete.part_name}"?`
                : 'Do you really want to delete this record?'}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeDeleteModal} isDisabled={deleting}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={() => void confirmDelete()} isLoading={deleting}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
